import type { VentasResumenProductoRow, VentasRpt01Row } from '../services/ventasInformesService';
import type { ClaveProductoResumen } from './agruparVentasResumenProducto';
import { normalizeMesKey } from './agruparVentasResumenProductoMensual';

/** Vacío = Todos. */
export type FiltroCategorizacion = '' | 'categorizado' | 'noCategorizado';

export function esFiltroCategorizacionActivo(
  filtro: FiltroCategorizacion
): filtro is 'categorizado' | 'noCategorizado' {
  return filtro === 'categorizado' || filtro === 'noCategorizado';
}

/** No Categorizado: interno (letra X) o tipo IVA N. */
export function esComprobanteInternoVenta(r: {
  letra?: string | null;
  tipoIva?: string | null;
}): boolean {
  const tipoIva = (r.tipoIva ?? '').toString().trim().toUpperCase();
  const letra = (r.letra ?? '').toString().trim().toUpperCase();
  return tipoIva === 'N' || letra === 'X';
}

/** Categorizado: todo excepto no Categorizado. */
export function esComprobanteCategorizado(r: {
  letra?: string | null;
  tipoIva?: string | null;
}): boolean {
  return !esComprobanteInternoVenta(r);
}

export function coincideFiltroCategorizacion(
  r: { letra?: string | null; tipoIva?: string | null },
  filtro: FiltroCategorizacion
): boolean {
  if (filtro === 'categorizado') return esComprobanteCategorizado(r);
  if (filtro === 'noCategorizado') return esComprobanteInternoVenta(r);
  return true;
}

export function isNotaCreditoDetalle(r: VentasRpt01Row): boolean {
  const nroTipo = String(r.nroTipoFormulario ?? '').trim();
  if (nroTipo === '3' || nroTipo === '4') return true;
  const tipU = (r.tipDescripcion ?? '').trim().toUpperCase();
  return tipU.includes('NOTA') && (tipU.includes('CRÉDITO') || tipU.includes('CREDITO'));
}

export function signoVentaResumenNC(r: VentasRpt01Row): number {
  return isNotaCreditoDetalle(r) ? -1 : 1;
}

function norm(val?: string | null): string {
  return (val ?? '').trim().toUpperCase();
}

export function claveComprobanteDetalle(r: VentasRpt01Row): string {
  return `${r.nroFormulario ?? ''}|${r.nroTipoFormulario ?? ''}|${r.letra ?? ''}`;
}

export function lineaCoincideClaveProducto(r: VentasRpt01Row, clave: ClaveProductoResumen): boolean {
  const producto = norm(clave.producto);
  if (producto) {
    const candidatos = [r.iteCodigoArticulo, r.codigoArticulo, r.iteDescripcion, r.descripcion]
      .map((x) => norm(x))
      .filter(Boolean);
    if (!candidatos.includes(producto)) return false;
  }
  if (norm(r.tipoArticulo) !== norm(clave.tipo)) return false;
  if (norm(r.nombreRamo) !== norm(clave.nombreRamo)) return false;
  if (norm(r.nombreLinea) !== norm(clave.nombreLinea)) return false;
  if (norm(r.nombreClase) !== norm(clave.nombreClase)) return false;
  return true;
}

export function lineaPasaFiltrosResumen(
  r: VentasRpt01Row,
  filtros: {
    tiposFormulario: string[];
    tipoArticulo?: string;
    nombreRamo?: string;
    nombreLinea?: string;
    nombreClase?: string;
    codigoArticulo?: string;
    filtroCategorizacion: FiltroCategorizacion;
  }
): boolean {
  const nroTipo = String(r.nroTipoFormulario ?? '').trim();
  if (filtros.tiposFormulario.length > 0 && !filtros.tiposFormulario.includes(nroTipo)) {
    return false;
  }
  if (filtros.tipoArticulo && norm(r.tipoArticulo) !== norm(filtros.tipoArticulo)) return false;
  if (filtros.nombreRamo && norm(r.nombreRamo) !== norm(filtros.nombreRamo)) return false;
  if (filtros.nombreLinea && norm(r.nombreLinea) !== norm(filtros.nombreLinea)) return false;
  if (filtros.nombreClase && norm(r.nombreClase) !== norm(filtros.nombreClase)) return false;
  const cod = (filtros.codigoArticulo ?? '').trim().toUpperCase();
  if (cod) {
    const hayCod = [r.iteCodigoArticulo, r.codigoArticulo]
      .some((x) => norm(x).includes(cod));
    if (!hayCod) return false;
  }
  return coincideFiltroCategorizacion(r, filtros.filtroCategorizacion);
}

export function agregarLineasAResumenMensual(
  lines: VentasRpt01Row[],
  clave: ClaveProductoResumen
): VentasResumenProductoRow[] {
  const map = new Map<string, VentasResumenProductoRow>();
  for (const r of lines) {
    const sign = signoVentaResumenNC(r);
    const mes = normalizeMesKey(r.fechaEmision ?? r.fecha) ?? '';
    const cantidad = sign * Number(r.iteCantidad ?? 0);
    const montoTotal = sign * Number(r.subtotalFinal ?? r.subtotal ?? 0);
    const costoTotal = sign * Number(r.iteCosto ?? 0) * Number(r.iteCantidad ?? 0);
    const pesoTotal = sign * Number(r.pesoTotalKg ?? 0);
    const volumenTotal = sign * Number(r.volumenTotalM3 ?? 0);
    const prev = map.get(mes);
    if (!prev) {
      map.set(mes, {
        mes: mes || undefined,
        producto: clave.producto,
        tipo: clave.tipo,
        nombreRamo: clave.nombreRamo,
        nombreLinea: clave.nombreLinea,
        nombreClase: clave.nombreClase,
        cantidad,
        montoTotal,
        costoTotal,
        pesoTotal,
        volumenTotal,
      });
      continue;
    }
    prev.cantidad += cantidad;
    prev.montoTotal += montoTotal;
    prev.costoTotal += costoTotal;
    prev.pesoTotal += pesoTotal;
    prev.volumenTotal += volumenTotal;
  }
  return Array.from(map.values());
}

export async function mapPool<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const idx = next;
      next += 1;
      out[idx] = await fn(items[idx]);
    }
  };
  const n = Math.max(1, Math.min(concurrency, items.length));
  await Promise.all(Array.from({ length: n }, () => worker()));
  return out;
}
